import {
  BlockRangeSchema,
  HMResourceVisibilitySchema,
  unpackedHmIdSchema,
  type UnpackedHypermediaId,
} from '@seed-hypermedia/client/hm-types'
import {z} from 'zod'
import type {SiteProfileTab, ViewRouteKey} from './utils/entity-id-url'
import {
  activityFilterToSlug,
  activitySlugToFilter,
  hmId,
  isSiteProfileTab,
  SITE_PROFILE_TABS,
} from './utils/entity-id-url'

export const PROFILE_TABS = [
  'profile', //default tab
  'membership',
  'followers',
  'following',
] as const
export type ProfileTab = (typeof PROFILE_TABS)[number]

export const defaultRoute: NavRoute = {key: 'onboarding'}

export const onboardingRouteSchema = z.object({key: z.literal('onboarding')})
export type OnboardingRoute = z.infer<typeof onboardingRouteSchema>

export const contactsRouteSchema = z.object({key: z.literal('contacts')})
export type ContactsRoute = z.infer<typeof contactsRouteSchema>

export const contactRouteSchema = z.object({
  key: z.literal('contact'),
  id: unpackedHmIdSchema,
})
export type ContactRoute = z.infer<typeof contactRouteSchema>

export const profileRouteSchema = z.object({
  key: z.literal('profile'),
  tab: z.enum(PROFILE_TABS).optional(),
  id: unpackedHmIdSchema,
})

export const siteProfileRouteSchema = z.object({
  key: z.literal('site-profile'),
  id: unpackedHmIdSchema,
  accountUid: z.string().optional(),
  tab: z.enum(SITE_PROFILE_TABS),
})
export type SiteProfileRoute = z.infer<typeof siteProfileRouteSchema>
export type ProfileRoute = z.infer<typeof profileRouteSchema>

const INSPECT_TARGET_VIEW_KEYS = [
  'activity',
  'comments',
  'collaborators',
  'directory',
  'feed',
  'all-documents',
  'metadata',
  ...SITE_PROFILE_TABS,
] as const
const inspectTargetViewSchema = z.enum(INSPECT_TARGET_VIEW_KEYS).optional()
/** Supported tabs for the inspector UI. */
export const INSPECT_TABS = [
  'document',
  'changes',
  'graph',
  'versions',
  'comments',
  'citations',
  'children',
  'authored-comments',
  'contacts',
  'capabilities',
] as const
/** Inspector tab key. */
export type InspectTab = (typeof INSPECT_TABS)[number]
const inspectTabSchema = z.enum(INSPECT_TABS).optional()

// Shared panel schemas for use in page-level routes
const activityPanelSchema = z.object({
  key: z.literal('activity'),
  id: unpackedHmIdSchema.optional(),
  autoFocus: z.boolean().optional(),
  filterEventType: z.array(z.string()).optional(),
  targetBlockId: z.string().optional(),
})

const commentsPanelSchema = z.object({
  key: z.literal('comments'),
  id: unpackedHmIdSchema.optional(),
  openComment: z.string().optional(),
  /** Last comment in the active reply chain. */
  replyCommentId: z.string().optional(),
})

const collaboratorsPanelSchema = z.object({
  key: z.literal('collaborators'),
  id: unpackedHmIdSchema.optional(),
})

const directoryPanelSchema = z.object({
  key: z.literal('directory'),
  id: unpackedHmIdSchema.optional(),
})

/** Tabs of the space Activity view. A missing tab means All updates. */
export const ACTIVITY_TABS = ['to-you', 'all-updates'] as const
/** One tab of the space Activity view. */
export type ActivityTab = (typeof ACTIVITY_TABS)[number]
const activityTabSchema = z.enum(ACTIVITY_TABS)

// The space Activity side panel. `id` is the space home.
const feedPanelSchema = z.object({
  key: z.literal('feed'),
  id: unpackedHmIdSchema.optional(),
  tab: activityTabSchema.optional(),
})

// Directory page panel options
const directoryPagePanelSchema = z.discriminatedUnion('key', [
  activityPanelSchema,
  commentsPanelSchema,
  collaboratorsPanelSchema,
  feedPanelSchema,
])

export const directoryRouteSchema = z.object({
  key: z.literal('directory'),
  id: unpackedHmIdSchema,
  panel: directoryPagePanelSchema.nullable().optional(),
})
export type DocumentDirectorySelection = z.infer<typeof directoryRouteSchema>

export const allDocumentsRouteSchema = z.object({
  key: z.literal('all-documents'),
  id: unpackedHmIdSchema,
})
export type AllDocumentsRoute = z.infer<typeof allDocumentsRouteSchema>

/** Route schema for the document inspector view. */
export const inspectRouteSchema = z.object({
  key: z.literal('inspect'),
  id: unpackedHmIdSchema,
  targetView: inspectTargetViewSchema,
  targetActivityFilter: z.array(z.string()).optional(),
  targetOpenComment: z.string().optional(),
  targetAccountUid: z.string().optional(),
  inspectTab: inspectTabSchema,
})
/** Navigation route for the document inspector view. */
export type InspectRoute = z.infer<typeof inspectRouteSchema>

/** Route schema for raw IPFS inspection inside the inspector. */
/** Edit a blob IN THE CONTEXT of a document's metadata field: publishing the
 * blob also publishes a metadata change on that document (no draft). */
export const inspectIpfsEditFieldSchema = z.object({
  /** The document (hm:// URL) whose metadata field references the blob. */
  docUrl: z.string(),
  /** The metadata key (e.g. `schemaDefinition`). */
  field: z.string(),
})
export type InspectIpfsEditField = z.infer<typeof inspectIpfsEditFieldSchema>

export const inspectIpfsRouteSchema = z.object({
  key: z.literal('inspect-ipfs'),
  ipfsPath: z.string(),
  editField: inspectIpfsEditFieldSchema.optional(),
})
/** Navigation route for raw IPFS inspection inside the inspector. */
export type InspectIpfsRoute = z.infer<typeof inspectIpfsRouteSchema>

/** A schema blob by CID — the full-page schema browser (`/hm/schema/<cid>`).
 * Bundled library schemas and user-published ones alike; refs are clickable. */
export const schemaRouteSchema = z.object({
  key: z.literal('schema'),
  /** The schema blob CID. Optional when `id` is set (resolved from the doc's schemaDefinition). */
  cid: z.string().optional(),
  /** The document that DEFINES this schema. With it, this is a document tool tab (like Attributes)
   * and the URL is the doc URL suffixed with /:schema; without it, the bare-CID browser page. */
  id: unpackedHmIdSchema.optional(),
})
export type SchemaRoute = z.infer<typeof schemaRouteSchema>

// Collaborators page panel options
const collaboratorsPagePanelSchema = z.discriminatedUnion('key', [
  activityPanelSchema,
  commentsPanelSchema,
  directoryPanelSchema,
  feedPanelSchema,
])

export const collaboratorsRouteSchema = z.object({
  key: z.literal('collaborators'),
  id: unpackedHmIdSchema,
  panel: collaboratorsPagePanelSchema.nullable().optional(),
})
export type CollaboratorsRoute = z.infer<typeof collaboratorsRouteSchema>

// Activity page panel options
const activityPagePanelSchema = z.discriminatedUnion('key', [
  commentsPanelSchema,
  collaboratorsPanelSchema,
  directoryPanelSchema,
  feedPanelSchema,
])

export const documentOptionsRouteSchema = z.object({
  key: z.literal('options'),
})
export type DocumentOptionsRoute = z.infer<typeof documentOptionsRouteSchema>

export const activityRouteSchema = z.object({
  key: z.literal('activity'),
  id: unpackedHmIdSchema,
  width: z.number().optional(),
  autoFocus: z.boolean().optional(),
  filterEventType: z.array(z.string()).optional(),
  targetBlockId: z.string().optional(),
  panel: activityPagePanelSchema.nullable().optional(),
})
export type ActivityRoute = z.infer<typeof activityRouteSchema>

// Comments page panel options
const commentsPagePanelSchema = z.discriminatedUnion('key', [
  activityPanelSchema,
  commentsPanelSchema,
  collaboratorsPanelSchema,
  directoryPanelSchema,
  feedPanelSchema,
])

export const commentsRouteSchema = z.object({
  key: z.literal('comments'),
  id: unpackedHmIdSchema,
  width: z.number().optional(),
  openComment: z.string().optional(),
  /** Last comment in the active reply chain. */
  replyCommentId: z.string().optional(),
  /** Version CID of the opened comment to view (an old version from its edit history). */
  openCommentVersion: z.string().optional(),
  targetBlockId: z.string().optional(),
  blockId: z.string().optional(),
  blockRange: BlockRangeSchema.nullable().optional(),
  autoFocus: z.boolean().optional(),
  isReplying: z.boolean().optional(),
  replyCommentVersion: z.string().optional(),
  rootReplyCommentVersion: z.string().optional(),
  panel: commentsPagePanelSchema.nullable().optional(),
})
export type CommentsRoute = z.infer<typeof commentsRouteSchema>

// Metadata page panel options
const metadataPagePanelSchema = z.discriminatedUnion('key', [
  activityPanelSchema,
  commentsPanelSchema,
  collaboratorsPanelSchema,
  directoryPanelSchema,
  feedPanelSchema,
])

export const metadataRouteSchema = z.object({
  key: z.literal('metadata'),
  id: unpackedHmIdSchema,
  panel: metadataPagePanelSchema.nullable().optional(),
  /** Open the tab with this binding schema ready to edit (drafting an empty struct when there is none). */
  focus: z.enum(['attributesSchema', 'childAttributesSchema']).optional(),
  /** Open the Add Raw Field dialog (from the document options menu). */
  addRawField: z.boolean().optional(),
})
export type MetadataRoute = z.infer<typeof metadataRouteSchema>

const documentPanelRoute = z.discriminatedUnion('key', [
  activityRouteSchema,
  commentsRouteSchema,
  directoryRouteSchema,
  collaboratorsRouteSchema,
  documentOptionsRouteSchema,
  metadataRouteSchema,
  feedPanelSchema,
])
export type DocumentPanelRoute = z.infer<typeof documentPanelRoute>
export type PanelSelectionOptions = DocumentPanelRoute['key']

/** Resolves an expanded document panel's title and the panel to reopen when it collapses. */
export function getExpandedDocumentPanel(route: NavRoute): {title: string; panel: DocumentPanelRoute} | null {
  switch (route.key) {
    case 'comments': {
      const {panel: _panel, ...panel} = route
      return {title: 'Comments', panel}
    }
    case 'metadata':
      return {title: 'Attributes', panel: {key: 'metadata', id: route.id}}
    case 'collaborators':
      return {title: 'People', panel: {key: 'collaborators', id: route.id}}
    case 'activity':
      if (activityFilterToSlug(route.filterEventType) === 'citations') {
        return {
          title: 'Connections',
          panel: {
            key: 'activity',
            id: route.id,
            filterEventType: route.filterEventType,
            targetBlockId: route.targetBlockId || route.id.blockRef || undefined,
          },
        }
      }
  }
  return null
}

export const documentRouteSchema = z.object({
  key: z.literal('document'),
  id: unpackedHmIdSchema,
  isBlockFocused: z.boolean().optional(),
  immediatelyPromptPush: z.boolean().optional(),
  immediatelyPromptNotifs: z.boolean().optional(),
  panel: documentPanelRoute.nullable().optional(),
})

// The full-page space Activity view. `id` is the space home.
export const feedRouteSchema = z.object({
  key: z.literal('feed'),
  id: unpackedHmIdSchema,
  tab: activityTabSchema.optional(),
  panel: documentPanelRoute.nullable().optional(),
})

export type DocumentRoute = z.infer<typeof documentRouteSchema>
export type FeedRoute = z.infer<typeof feedRouteSchema>

export const draftRouteSchema = z.object({
  key: z.literal('draft'),
  id: z.string(),
  locationUid: z.string().optional(),
  locationPath: z.array(z.string()).optional(),
  editUid: z.string().optional(),
  editPath: z.array(z.string()).optional(),
  deps: z.array(z.string()).optional(),
  panel: documentPanelRoute.nullable().optional(),
  isWelcomeDraft: z.boolean().optional(),
  visibility: HMResourceVisibilitySchema.optional(),
})
export type DraftRoute = z.infer<typeof draftRouteSchema>

export const bookmarksSchema = z.object({
  key: z.literal('bookmarks'),
})
export type BookmarksRoute = z.infer<typeof bookmarksSchema>

export const draftsSchema = z.object({
  key: z.literal('drafts'),
})
export type DraftsRoute = z.infer<typeof draftsSchema>

export const settingsTabSchema = z.enum(['general', 'sync', 'app-info', 'agent-servers', 'advanced'])
export type SettingsTab = z.infer<typeof settingsTabSchema>

export const settingsRouteSchema = z.object({
  key: z.literal('settings'),
  tab: settingsTabSchema.optional(),
})
export type SettingsRoute = z.infer<typeof settingsRouteSchema>

export const accountSettingsTabSchema = z.enum(['devices', 'notifications'])
export type AccountSettingsTab = z.infer<typeof accountSettingsTabSchema>

/** Route schema for the desktop Account Settings page (manage the logged-in account). */
export const accountSettingsRouteSchema = z.object({
  key: z.literal('account-settings'),
  accountUid: z.string().optional(),
  tab: accountSettingsTabSchema.optional(),
  /** When 'vault', the page shows vault-wide settings instead of a single account. */
  view: z.enum(['vault']).optional(),
})
/** Navigation route for the desktop Account Settings page. */
export type AccountSettingsRoute = z.infer<typeof accountSettingsRouteSchema>

export const siteSettingsTabSchema = z.enum([
  'identity',
  'navigation',
  'web-domain',
  'members',
  'writers',
  'email-subscribers',
  'agents',
])
export type SiteSettingsTab = z.infer<typeof siteSettingsTabSchema>

export const siteSettingsRouteSchema = z.object({
  key: z.literal('site-settings'),
  id: unpackedHmIdSchema,
  tab: siteSettingsTabSchema.optional(),
  /** The custom domain whose durable setup progress is being viewed. */
  domain: z.string().optional(),
})
export type SiteSettingsRoute = z.infer<typeof siteSettingsRouteSchema>

/**
 * Route for the site owner's email subscribers page. On web this is the
 * current site; on desktop accountUid selects which owned site to show.
 */
export const siteSettingsEmailsRouteSchema = z.object({
  key: z.literal('site-settings-emails'),
  accountUid: z.string().optional(),
})
export type SiteSettingsEmailsRoute = z.infer<typeof siteSettingsEmailsRouteSchema>

/** Route schema for the desktop Agents page. */
export const agentsRouteSchema = z.object({
  key: z.literal('agents'),
})
/** Navigation route for the desktop Agents page. */
export type AgentsRoute = z.infer<typeof agentsRouteSchema>

/** Route schema for one configured agents server. */
export const agentServerRouteSchema = z.object({
  key: z.literal('agent-server'),
  serverUrl: z.string(),
})
/** Navigation route for one configured agents server. */
export type AgentServerRoute = z.infer<typeof agentServerRouteSchema>

/** Route schema for one server-hosted agent. */
export const agentRouteSchema = z.object({
  key: z.literal('agent'),
  agentId: z.string(),
  serverUrl: z.string().optional(),
  tab: z.enum(['sessions', 'triggers', 'memory', 'tools', 'prompt', 'collaborators', 'settings']).optional(),
  triggerId: z.string().optional(),
  /** Memory tab: the file to open, so a `~/memory/…` link in a tool row lands on that file. */
  memoryPath: z.string().optional(),
})
/** Navigation route for one server-hosted agent. */
export type AgentRoute = z.infer<typeof agentRouteSchema>

/** Route schema for one server-hosted agent session. */
export const agentSessionRouteSchema = z.object({
  key: z.literal('agent-session'),
  sessionId: z.string(),
  serverUrl: z.string().optional(),
  agentId: z.string().optional(),
})
/** Navigation route for one server-hosted agent session. */
export type AgentSessionRoute = z.infer<typeof agentSessionRouteSchema>

/** Route schema for one durable agent run (a script or delegated model run). */
export const agentRunRouteSchema = z.object({
  key: z.literal('agent-run'),
  runId: z.string(),
  serverUrl: z.string().optional(),
  agentId: z.string().optional(),
})
/** Navigation route for one durable agent run. */
export type AgentRunRoute = z.infer<typeof agentRunRouteSchema>

/** Route schema for the desktop API inspector. */
export const apiInspectorRouteSchema = z.object({
  key: z.literal('api-inspector'),
})
/** Navigation route for the desktop API inspector. */
export type ApiInspectorRoute = z.infer<typeof apiInspectorRouteSchema>

/** Route for the desktop Query Documents playground. */
export const queryDocumentsRouteSchema = z.object({
  key: z.literal('query-documents'),
})
/** Navigation route for the desktop Query Documents playground. */
export type QueryDocumentsRoute = z.infer<typeof queryDocumentsRouteSchema>

/** Route for the Explore search prototype. */
export const exploreRouteSchema = z.object({
  key: z.literal('explore'),
  context: z.discriminatedUnion('type', [
    z.object({type: z.literal('node')}),
    z.object({type: z.literal('site'), id: unpackedHmIdSchema}),
  ]),
  q: z.string().optional(),
  sort: z.enum(['relevance', 'recently_updated', 'newest', 'oldest', 'title']).optional(),
})
/** Navigation route for the Explore search prototype. */
export type ExploreRoute = z.infer<typeof exploreRouteSchema>

export const draftRebaseRouteSchema = z.object({
  key: z.literal('draft-rebase'),
  documentId: z.string(),
  sourceVersion: z.string(),
  targetVersion: z.string(),
})

export const libraryRouteSchema = z.object({
  key: z.literal('library'),
  expandedIds: z.array(z.string()).optional(),
  displayMode: z.enum(['all', 'subscribed', 'bookmarks']).optional(),
  grouping: z.enum(['site', 'none']).optional(),
})
export type LibraryRoute = z.infer<typeof libraryRouteSchema>

/** An external website displayed in the experimental desktop browser. */
export const webRouteSchema = z.object({
  key: z.literal('web'),
  url: z
    .string()
    .url()
    .refine((url) => /^https?:\/\//.test(url)),
  title: z.string().optional(),
  browserId: z.number().optional(),
  historyIndex: z.number().int().nonnegative().optional(),
})

export const navRouteSchema = z.discriminatedUnion('key', [
  webRouteSchema,
  onboardingRouteSchema,
  libraryRouteSchema,
  contactsRouteSchema,
  profileRouteSchema,
  siteProfileRouteSchema,
  contactRouteSchema,
  settingsRouteSchema,
  accountSettingsRouteSchema,
  siteSettingsRouteSchema,
  siteSettingsEmailsRouteSchema,
  documentRouteSchema,
  draftRouteSchema,
  draftRebaseRouteSchema,
  bookmarksSchema,
  draftsSchema,
  agentsRouteSchema,
  agentServerRouteSchema,
  agentRouteSchema,
  agentSessionRouteSchema,
  agentRunRouteSchema,
  apiInspectorRouteSchema,
  queryDocumentsRouteSchema,
  exploreRouteSchema,
  feedRouteSchema,
  allDocumentsRouteSchema,
  inspectRouteSchema,
  inspectIpfsRouteSchema,
  schemaRouteSchema,
  directoryRouteSchema,
  collaboratorsRouteSchema,
  activityRouteSchema,
  commentsRouteSchema,
  metadataRouteSchema,
])
export type NavRoute = z.infer<typeof navRouteSchema>

/** Returns a version-independent visited entity, keeping profiles distinct from home documents. */
export function getRecentsRouteEntityUrl(route: NavRoute) {
  if (route.key === 'profile' || route.key === 'site-profile') {
    const uid = route.key === 'site-profile' ? route.accountUid ?? route.id.uid : route.id.uid
    return hmId(uid, {path: [':profile']}).id
  }
  // this is used to uniquely identify an item for the recents list. So it references the entity without specifying version
  if (route.key === 'document') return route.id.id
  if (route.key === 'inspect') return route.id.id
  if (route.key === 'inspect-ipfs') return null
  // comments do not show up in the recents list, we do not know how to display them
  return null
}

export type DocSelectionOption = {
  key: PanelSelectionOptions
  label: string
}

export function getRoutePanel(route: NavRoute): NavRoute | null {
  let panel: DocumentPanelRoute | undefined | null = undefined
  let routeId: z.infer<typeof unpackedHmIdSchema> | undefined = undefined
  if (route.key === 'document') {
    panel = route.panel
    routeId = route.id
  } else if (route.key === 'draft') {
    panel = route.panel
  } else if (route.key === 'feed') {
    panel = route.panel
    routeId = route.id
  } else if (route.key === 'directory') {
    panel = route.panel as DocumentPanelRoute | null
    routeId = route.id
  } else if (route.key === 'collaborators') {
    panel = route.panel as DocumentPanelRoute | null
    routeId = route.id
  } else if (route.key === 'activity') {
    panel = route.panel as DocumentPanelRoute | null
    routeId = route.id
  } else if (route.key === 'comments') {
    panel = route.panel as DocumentPanelRoute | null
    routeId = route.id
  } else if (route.key === 'metadata') {
    panel = route.panel as DocumentPanelRoute | null
    routeId = route.id
  }
  if (!panel) return null
  // Ensure panel has id from parent route if not set
  if (routeId && panel.key !== 'options' && !panel.id) {
    return {...panel, id: routeId} as NavRoute
  }
  return panel as NavRoute
}

/** Parses the tab segment of an Activity URL. */
export function activityTabFromSlug(slug: string | undefined): ActivityTab | undefined {
  return slug === 'to-you' ? 'to-you' : undefined
}

/** The space a route belongs to, or null for pages outside any space. */
export function getRouteSpaceUid(route: NavRoute): string | null {
  switch (route.key) {
    case 'document':
    case 'feed':
    case 'directory':
    case 'collaborators':
    case 'activity':
    case 'comments':
    case 'metadata':
    case 'all-documents':
    case 'site-profile':
    case 'site-settings':
      return route.id.uid
    case 'explore':
      return route.context.type === 'site' ? route.context.id.uid : null
    default:
      return null
  }
}

type ActivityPanelHostRoute = Extract<
  NavRoute,
  {key: 'document' | 'directory' | 'collaborators' | 'activity' | 'comments' | 'metadata'}
>

function isActivityPanelHost(route: NavRoute): route is ActivityPanelHostRoute {
  return (
    route.key === 'document' ||
    route.key === 'directory' ||
    route.key === 'collaborators' ||
    route.key === 'activity' ||
    route.key === 'comments' ||
    route.key === 'metadata'
  )
}

/** Whether the space Activity view is showing, as the side panel or as the full page. */
export function isActivityOpen(route: NavRoute): boolean {
  return route.key === 'feed' || (isActivityPanelHost(route) && route.panel?.key === 'feed')
}

/**
 * Where the bell leads from a route. On pages with a right panel it toggles the Activity right panel.
 * On other space pages it opens the full Activity page. Returns 'collapse' on the full page and null outside any space.
 */
export function getActivityBellTarget(route: NavRoute, tab: ActivityTab = 'to-you'): NavRoute | 'collapse' | null {
  const spaceUid = getRouteSpaceUid(route)
  if (!spaceUid) return null
  if (route.key === 'feed') return 'collapse'
  const spaceId = hmId(spaceUid)
  if (isActivityPanelHost(route)) {
    if (route.panel?.key === 'feed') return {...route, panel: null}
    return {...route, panel: {key: 'feed', id: spaceId, tab}}
  }
  return {key: 'feed', id: spaceId, tab}
}

function replacePanelDocumentId(
  panel: DocumentPanelRoute | undefined | null,
  targetId: UnpackedHypermediaId,
): DocumentPanelRoute | undefined | null {
  if (!panel) return panel

  // The Activity panel shows a space, so it follows the target's space rather than pointing at the document.
  if (panel.key === 'feed') return {...panel, id: hmId(targetId.uid)}

  const nextPanel = 'panel' in panel ? replacePanelDocumentId(panel.panel as DocumentPanelRoute | null, targetId) : null

  if ('id' in panel) {
    return {
      ...panel,
      id: targetId,
      ...('panel' in panel ? {panel: nextPanel} : {}),
    } as DocumentPanelRoute
  }

  if ('panel' in panel) {
    return {
      ...panel,
      panel: nextPanel,
    } as DocumentPanelRoute
  }

  return panel
}

/**
 * Rewrites a document-style navigation route so it points at a new document ID.
 * Preserves the current view/panel state while updating all embedded document IDs.
 */
export function replaceRouteDocumentId(route: NavRoute, targetId: UnpackedHypermediaId): NavRoute {
  switch (route.key) {
    case 'document':
    case 'feed':
    case 'directory':
    case 'collaborators':
    case 'activity':
    case 'comments':
    case 'metadata':
      return {
        ...route,
        id: targetId,
        panel: replacePanelDocumentId(route.panel as DocumentPanelRoute | null, targetId) as any,
      }
    case 'all-documents':
      return {
        ...route,
        id: targetId,
      }
    case 'explore':
      return route.context.type === 'site'
        ? {
            ...route,
            context: {...route.context, id: targetId},
          }
        : route
    case 'site-profile':
      return {
        ...route,
        id: targetId,
      }
    default:
      return route
  }
}

/**
 * Create a DocumentPanelRoute from a panel param string
 * Supports extended formats:
 * - "comments/COMMENT_ID" for specific comment open in panel
 * - "comments/BLOCKID" for block-specific comments
 * Backward compat: "comment/COMMENT_ID" and "discussions/BLOCKID" also accepted
 */
function createPanelRoute(panelParam: string, docId: UnpackedHypermediaId): DocumentPanelRoute {
  // Check for comments/COMMENT_ID or comment/COMMENT_ID format (most specific)
  if (panelParam.startsWith('comments/') || panelParam.startsWith('comment/')) {
    const prefix = panelParam.startsWith('comments/') ? 'comments/' : 'comment/'
    const openComment = panelParam.slice(prefix.length)
    return {key: 'comments' as const, id: docId, openComment}
  }

  // Check for activity/<filter-slug> format
  if (panelParam.startsWith('activity/')) {
    const slug = panelParam.slice('activity/'.length)
    const filterEventType = activitySlugToFilter(slug)
    return {key: 'activity', id: docId, filterEventType}
  }

  // The space Activity panel: "feed" for all updates, "feed/to-you" for notifications.
  if (panelParam === 'feed' || panelParam.startsWith('feed/')) {
    return {key: 'feed', id: hmId(docId.uid), tab: activityTabFromSlug(panelParam.slice('feed/'.length))}
  }

  // Check for discussions/BLOCKID format (backward compat)
  if (panelParam.startsWith('discussions/')) {
    const targetBlockId = panelParam.slice('discussions/'.length)
    return {key: 'comments' as const, id: docId, targetBlockId}
  }

  switch (panelParam) {
    case 'activity':
      return {key: 'activity', id: docId}
    case 'comments':
    case 'discussions': // backward compat
      return {key: 'comments', id: docId}
    case 'directory':
      return {key: 'directory', id: docId}
    case 'collaborators':
      return {key: 'collaborators', id: docId}
    case 'metadata':
      return {key: 'metadata', id: docId}
    case 'options':
      return {key: 'options'}
    default:
      // Fallback for unknown panel types
      return {key: 'comments', id: docId}
  }
}

/**
 * Convert docId + viewTerm + panelParam into a NavRoute
 * Used by web to initialize navigation context from URL
 * panelParam supports extended format like "comments/BLOCKID" or "comments/COMMENT_ID"
 * Backward compat: "discussions" viewTerm and "comment/" panel prefix also accepted
 */
export function createDocumentNavRoute(
  docId: UnpackedHypermediaId,
  viewTerm?: ViewRouteKey | null,
  panelParam?: string | null,
  openComment?: string | null,
  accountUid?: string | null,
  openCommentVersion?: string | null,
  replyCommentId?: string | null,
): NavRoute {
  // Create properly typed panel route if panelParam provided.
  // Cast needed: each route schema has a narrow panel union (excluding itself),
  // but createPanelRoute returns the broader DocumentPanelRoute.
  // Same pattern used in document-tools.tsx panelFor().
  let panel = (panelParam ? createPanelRoute(panelParam, docId) : null) as any
  if (viewTerm === 'comments' && panel?.key === 'comments') panel = null
  if (panel?.key === 'comments' && panel.openComment && replyCommentId) {
    panel = {...panel, replyCommentId}
  }

  if (isSiteProfileTab(viewTerm)) {
    return {key: 'site-profile', id: docId, accountUid: accountUid || undefined, tab: viewTerm as SiteProfileTab}
  }

  switch (viewTerm) {
    case 'activity': {
      // When activity is the main view, parse filter slug from panelParam
      // URL format: /:activity?panel=activity/versions -> panelParam="activity/versions"
      let filterEventType: string[] | undefined
      // Only use panelParam as activity filter when the panel itself is activity-type
      if (panelParam?.startsWith('activity/')) {
        const slug = panelParam.slice('activity/'.length)
        filterEventType = activitySlugToFilter(slug)
        // Activity filter consumed as filterEventType, no side panel
        return {key: 'activity', id: docId, filterEventType, panel: null}
      }
      // Non-activity panel preserved as side panel
      return {key: 'activity', id: docId, filterEventType, panel}
    }
    case 'comments':
      // /:comments/UID/TSID → comments main with openComment
      if (openComment) {
        return {
          key: 'comments',
          id: docId,
          openComment,
          openCommentVersion: openCommentVersion || undefined,
          ...(replyCommentId ? {replyCommentId} : {}),
          panel,
        }
      }
      // Preserve non-comments panel (e.g. activity, collaborators)
      return {key: 'comments', id: docId, panel}
    case 'directory':
      return {key: 'directory', id: docId, panel}
    case 'collaborators':
      return {key: 'collaborators', id: docId, panel}
    case 'feed':
      // URL format: /:feed/to-you -> panelParam="feed/to-you", consumed as the page's tab
      if (panelParam?.startsWith('feed/')) {
        return {key: 'feed', id: docId, tab: activityTabFromSlug(panelParam.slice('feed/'.length)), panel: null}
      }
      return {key: 'feed', id: docId, panel}
    case 'all-documents':
      return {key: 'all-documents', id: docId}
    case 'explore':
      return {key: 'explore', context: {type: 'site', id: docId}}
    case 'site-settings':
      return {key: 'site-settings', id: docId}
    case 'metadata':
      return {key: 'metadata', id: docId, panel}
    case 'schema':
      return {key: 'schema', id: docId}
    default: {
      // ?panel=comments/COMMENT_ID (no viewTerm) → document main + comments right panel
      return {key: 'document', id: docId, panel}
    }
  }
}

/** Creates an inspector route for a document plus an optional nested document view. */
export function createInspectNavRoute(
  docId: UnpackedHypermediaId,
  targetView?: ViewRouteKey | null,
  panelParam?: string | null,
  openComment?: string | null,
  accountUid?: string | null,
  inspectTab?: InspectTab | null,
): InspectRoute {
  const targetActivityFilter =
    targetView === 'activity' && panelParam?.startsWith('activity/')
      ? activitySlugToFilter(panelParam.slice('activity/'.length))
      : undefined

  const route: InspectRoute = {
    key: 'inspect',
    id: docId,
  }
  if (targetView && targetView !== 'site-settings' && targetView !== 'explore' && targetView !== 'schema')
    route.targetView = targetView
  if (targetActivityFilter) route.targetActivityFilter = targetActivityFilter
  if (targetView === 'comments' && openComment) route.targetOpenComment = openComment
  if (isSiteProfileTab(targetView) && accountUid) route.targetAccountUid = accountUid
  if (inspectTab && inspectTab !== 'document') route.inspectTab = inspectTab
  return route
}

/** Creates an inspector route for a raw IPFS object. */
export function createInspectIpfsNavRoute(ipfsPath: string, editField?: InspectIpfsEditField): InspectIpfsRoute {
  return {
    key: 'inspect-ipfs',
    ipfsPath,
    ...(editField ? {editField} : {}),
  }
}

/** Converts an inspector route back into the equivalent non-inspector app route. */
export function createRouteFromInspectNavRoute(route: InspectRoute, inspectTab?: InspectTab | null): NavRoute {
  switch (inspectTab) {
    case 'changes':
    case 'graph':
      return {
        key: 'activity',
        id: route.id,
        filterEventType: activitySlugToFilter('versions'),
        panel: null,
      }
    case 'versions':
      return {
        key: 'comments',
        id: route.id,
        openComment: route.targetOpenComment,
        panel: null,
      }
    case 'comments':
      return {
        key: 'comments',
        id: route.id,
        openComment: route.targetOpenComment,
        panel: null,
      }
    case 'citations':
      return {
        key: 'activity',
        id: route.id,
        filterEventType: activitySlugToFilter('citations'),
        panel: null,
      }
    case 'children':
      return {key: 'directory', id: route.id, panel: null}
    case 'capabilities':
      return {key: 'collaborators', id: route.id, panel: null}
  }

  switch (route.targetView) {
    case 'feed':
      return {key: 'feed', id: route.id, panel: null}
    case 'directory':
      return {key: 'directory', id: route.id, panel: null}
    case 'collaborators':
      return {key: 'collaborators', id: route.id, panel: null}
    case 'comments':
      return {
        key: 'comments',
        id: route.id,
        openComment: route.targetOpenComment,
        panel: null,
      }
    case 'activity':
      return {
        key: 'activity',
        id: route.id,
        filterEventType: route.targetActivityFilter,
        panel: null,
      }
    case 'profile':
    case 'membership':
    case 'followers':
    case 'following':
      return {
        key: 'site-profile',
        id: route.id,
        accountUid: route.targetAccountUid,
        tab: route.targetView,
      }
    default:
      return {key: 'document', id: route.id, panel: null}
  }
}

/** Wraps a document-like route so it opens inside the inspector. */
export function createInspectNavRouteFromRoute(route: NavRoute): InspectRoute | InspectIpfsRoute | null {
  switch (route.key) {
    case 'inspect':
    case 'inspect-ipfs':
      return route
    case 'document':
      if (route.panel?.key === 'comments') {
        return createInspectNavRoute(route.id, 'comments', null, route.panel.openComment)
      }
      if (route.panel?.key === 'activity') {
        const activitySlug = activityFilterToSlug(route.panel.filterEventType)
        return createInspectNavRoute(route.id, 'activity', activitySlug ? `activity/${activitySlug}` : null)
      }
      if (route.panel?.key === 'directory') {
        return createInspectNavRoute(route.id, 'directory')
      }
      if (route.panel?.key === 'collaborators') {
        return createInspectNavRoute(route.id, 'collaborators')
      }
      return createInspectNavRoute(route.id)
    case 'feed':
      return createInspectNavRoute(route.id, 'feed')
    case 'directory':
      return createInspectNavRoute(route.id, 'directory')
    case 'collaborators':
      return createInspectNavRoute(route.id, 'collaborators')
    case 'comments':
      return createInspectNavRoute(route.id, 'comments', null, route.openComment)
    case 'activity': {
      const activitySlug = activityFilterToSlug(route.filterEventType)
      return createInspectNavRoute(route.id, 'activity', activitySlug ? `activity/${activitySlug}` : null)
    }
    case 'site-profile':
      return createInspectNavRoute(route.id, route.tab, null, null, route.accountUid)
    case 'profile':
    case 'contact':
      return createInspectNavRoute(route.id)
    case 'draft':
      // A draft editing an existing document inspects that document; brand-new drafts have nothing published to inspect.
      return route.editUid ? createInspectNavRoute(hmId(route.editUid, {path: route.editPath ?? null})) : null
    case 'schema':
      // A schema page inspects its raw blob. Without a CID (doc-resolved form, still loading)
      // there is nothing addressable yet.
      return route.cid ? createInspectIpfsNavRoute(route.cid) : null
    default:
      return null
  }
}
